/**
 * Helper function to extract a localized field from an entity that has a `translations` array.
 * Assumes the translations array objects have a `locale` field (e.g., 'en' or 'ar').
 *
 * @param entity The data object containing the field and a translations array
 * @param field The key of the field to extract (e.g., 'name', 'description')
 * @param currentLanguage The current active language code from i18n.language
 * @returns The localized string, falling back to the entity's base field if not found
 */
export const getLocalizedField = (entity: any, field: string, currentLanguage: string): string => {
    if (!entity) return '';

    // Check if translations array exists
    if (entity.translations && Array.isArray(entity.translations)) {
        // 1. Try exact match for the requested language
        const exactTranslation = entity.translations.find((t: any) => t.locale === currentLanguage);
        if (exactTranslation && exactTranslation[field]) {
            return exactTranslation[field];
        }

        // 2. If requested language isn't found, try to find an English translation as a safe fallback
        const enTranslation = entity.translations.find((t: any) => t.locale === 'en');
        if (enTranslation && enTranslation[field]) {
            return enTranslation[field];
        }

        // 3. If English isn't found, try Arabic
        const arTranslation = entity.translations.find((t: any) => t.locale === 'ar');
        if (arTranslation && arTranslation[field]) {
            return arTranslation[field];
        }
    }

    // 4. Ultimate fallback to base field on the entity itself (which might be the default CMS language)
    return entity[field] || '';
};
